import { MOCK_ENABLED } from "./mock.ts";
import { recordSentEmails } from "./email-storage.ts";

const BASE_URL =
  (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ??
  "https://api.currency-core.com";

export const ALLOWED_SENDER_EMAILS = [
  "mail@currencycore.dev",
  "mail@currency-core.com",
] as const;

export type SenderEmail = typeof ALLOWED_SENDER_EMAILS[number];

export const DEFAULT_SENDER_EMAIL: SenderEmail = "mail@currencycore.dev";

export interface SenderOption {
  email: SenderEmail;
  label: string;
  domain: string;
  isDefault?: boolean;
}

export const SENDER_OPTIONS: SenderOption[] = [
  {
    email: "mail@currencycore.dev",
    label: "CurrencyCore (mail@currencycore.dev) - Default",
    domain: "currencycore.dev",
    isDefault: true,
  },
  {
    email: "mail@currency-core.com",
    label: "CurrencyCore (mail@currency-core.com)",
    domain: "currency-core.com",
  },
];

export interface RecipientInput {
  email: string;
  name?: string | null;
  subject?: string;
  html?: string;
  text?: string;
}

export interface SendEmailPayload {
  recipients: RecipientInput[];
  subject: string;
  html: string;
  text?: string;
  templateId?: string;
  templateName?: string;
  fromEmail?: string;
}

export interface SendEmailResult {
  ok: boolean;
  sentCount: number;
  failedCount: number;
  errors?: { email: string; error: string }[];
}

/**
 * Dispatch an outreach email to one or more recipients from mail@currency-core.com.
 * In mock mode (VITE_MOCK_AUTH=true), simulates dispatch with realistic latency.
 * In live mode, posts to /admin/email/send.
 * In both modes, automatically records to local sent history.
 */
export async function sendOutreachEmail(payload: SendEmailPayload): Promise<SendEmailResult> {
  const { recipients, subject, html, text, templateId, templateName, fromEmail } = payload;
  const activeSender = fromEmail ?? DEFAULT_SENDER_EMAIL;

  if (!recipients.length) {
    throw new Error("No recipients specified.");
  }

  if (MOCK_ENABLED) {
    // Simulate network delay in mock mode
    await new Promise((resolve) => setTimeout(resolve, 600));

    // Record mock sends to sent history
    recordSentEmails(
      recipients.map((r) => ({
        recipientEmail: r.email,
        recipientName: r.name ?? null,
        senderEmail: activeSender,
        subject: r.subject ?? subject,
        templateId: templateId ?? "custom",
        templateName: templateName ?? "Custom Email",
        status: "sent",
      })),
    );

    return {
      ok: true,
      sentCount: recipients.length,
      failedCount: 0,
    };
  }

  // Live API dispatch
  const res = await fetch(`${BASE_URL}/admin/email/send`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      fromEmail: activeSender,
      recipients: recipients.map((r) => ({
        email: r.email,
        name: r.name ?? undefined,
        subject: r.subject,
        html: r.html,
        text: r.text,
      })),
      subject,
      html,
      text,
      templateId,
    }),
  });

  if (!res.ok) {
    const errorBody = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
    throw new Error(errorBody.error?.message ?? `Failed to send email (${res.status} ${res.statusText})`);
  }

  const result = (await res.json()) as SendEmailResult;

  // Record to local sent history
  const failedMap = new Set((result.errors ?? []).map((e) => e.email));
  recordSentEmails(
    recipients.map((r) => ({
      recipientEmail: r.email,
      recipientName: r.name ?? null,
      senderEmail: activeSender,
      subject: r.subject ?? subject,
      templateId: templateId ?? "custom",
      templateName: templateName ?? "Custom Email",
      status: failedMap.has(r.email) ? "failed" : "sent",
      error: result.errors?.find((e) => e.email === r.email)?.error ?? null,
    })),
  );

  return result;
}
